/* UI-DESIGN: LB-80 the invitation and RSVP share the lightbox room.
   Show the actual party before asking for details, without an envelope gate.
   Query, fields, validation and registration payload remain unchanged. */
import { useState, useEffect } from "react";
import { useLazyGetGameQuery, useRegisterMutation } from "../../reducers/apiSlice";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useToast } from "../../components/ui/toast";
import convertErrorMessage from "../../utils/convertErrorMessage";
import Input from "../../components/input";
import IdForm from "./IdForm";
import { useSelector } from "react-redux";
import type { RootState } from "../../store/store";
import LightboxSurface from "../../components/lightbox/LightboxSurface";
import GlassPanel from "../../components/lightbox/GlassPanel";
import LightboxButton from "../../components/lightbox/LightboxButton";
import CasePrint from "../../components/lightbox/CasePrint";
import { initialTypeface } from "../../components/lightbox/typefaces";
import { ArrowRight, Check } from "@gravity-ui/icons";
import styles from "./Register.module.scss";

interface RegisterForm {
  name: string;
  email: string;
  phone_number: string;
  group_preference: string;
}

const Register = () => {
  const [searchParams] = useSearchParams();
  const game = useSelector((state: RootState) => state.game.game);
  const gameId = searchParams.get("gameId");
  const [trigger, { isLoading, isError }] = useLazyGetGameQuery();
  const [registerPlayer, { isLoading: isRegistering }] = useRegisterMutation();
  const [submitted, setSubmitted] = useState(false);
  const { triggerToast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    if (gameId) {
      trigger(gameId).catch(() => {
        navigate("/register");
      });
    }
  /* trigger/navigate are stable refs; effect still only re-runs when gameId changes */
  }, [gameId, trigger, navigate]);

  const { register, handleSubmit, formState: { errors } } = useForm<RegisterForm>({
    defaultValues: {
      name: "",
      email: "",
      phone_number: "",
      group_preference: "Group 1"
    },
  });

  const onSubmit = (data: RegisterForm) => {
    registerPlayer({ ...data, game: gameId }).then((res: { error?: unknown }) => {
      if (res.error) {
        triggerToast({
          title: "Error",
          description: convertErrorMessage(res.error)
        });
      } else {
        setSubmitted(true);
      }
    });
  };

  const hostName = [game?.host?.first_name, game?.host?.last_name].filter(Boolean).join(" ");

  return (
    <LightboxSurface mode="static" typeface={initialTypeface()} onUnavailable={() => undefined}>
      <main className={styles.page}>
        {isLoading ? (
          <GlassPanel className={styles.statusScreen}>
            <p role="status">Opening your invitation…</p>
          </GlassPanel>
        ) : !gameId || !game || isError || game.objectId !== gameId ? (
          <IdForm invalidLink={!!gameId && isError} />
        ) : (
          <div className={styles.layout}>
            <section className={styles.invitation} aria-labelledby="invitation-title">
              {/* UI-DESIGN: 2026-10-25 (Taco) the case photo makes each invite its own case file */}
              <CasePrint className={styles.print} name={game.mystery.name} />
              <p className={styles.invited}>You're invited to</p>
              <h1 id="invitation-title" className={styles.title}>{game.name || game.mystery.name}</h1>
              <p className={styles.caseName}>{game.mystery.name}</p>
              {hostName && <p className={styles.host}>Hosted by {hostName}</p>}
              {game.mystery.description && <p className={styles.description}>{game.mystery.description}</p>}
              <p className={styles.details}>Ask your host for the time and place.</p>
            </section>

            {!submitted ? (
              <GlassPanel as="form" className={styles.formCard} onSubmit={handleSubmit(onSubmit)} noValidate>
                <h2 className={styles.formTitle}>Count me in</h2>
                <p className={styles.formSubtitle}>Leave your details so your host knows you're coming.</p>
            <Input
              label="Your name"
              autoComplete="name"
              labelClassName={styles.fieldLabel}
              inputClassName={styles.field}
              register={register("name", { required: "Name is required" })}
              placeholder="Enter your name"
              error={errors.name?.message}
            />
            <Input
              label="Email address"
              type="email"
              autoComplete="email"
              inputMode="email"
              labelClassName={styles.fieldLabel}
              inputClassName={styles.field}
              register={register("email", {
                required: "Email is required",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Invalid email address"
                }
              })}
              placeholder="Enter your email"
              error={errors.email?.message}
            />
            <Input
              label="Phone number"
              autoComplete="tel"
              inputMode="tel"
              labelClassName={styles.fieldLabel}
              inputClassName={styles.field}
              register={register("phone_number", {
                required: "Phone number is required",
                validate: (value: string) => {
                  if (!/^\+1\d{10}$/.test(value)) {
                    return "Invalid phone number. Please enter a 10 digit number starting with +1.";
                  }
                  return true;
                }
              })}
              placeholder="+1 followed by 10 digits"
              error={errors.phone_number?.message}
            />


                <LightboxButton type="submit" isDisabled={isRegistering} trailing={isRegistering ? undefined : <ArrowRight />}>
                  {isRegistering ? "Joining…" : "Join the party"}
                </LightboxButton>
              </GlassPanel>
            ) : (
              <GlassPanel className={styles.formCard} role="status">
                <div className={styles.successMark} aria-hidden><Check width={24} height={24} /></div>
                <h2 className={styles.formTitle}>You're on the guest list</h2>
                <p className={styles.formSubtitle}>Your RSVP is saved. Your host will guide you through what comes next.</p>
              </GlassPanel>
            )}
          </div>
        )}
      </main>
    </LightboxSurface>
  );
};

export default Register;
